import { Button, ModalBody, ModalContent, ModalFooter, useDisclosure } from '@nextui-org/react'
import { useEffect } from 'react'
import useHomeAddGroupStore from '../../store/home/useHomeAddGroupStore'
import { useGroupStore } from '../../store/group/useGroupStore'
import { BaseDivider, BaseModal, BaseModalHeader } from '../common/Modal'
import { ModalText } from '../common/Text'
import { InputGroupName } from './InputGroupName'
import { createGroup } from '../../api/group/group.service'

interface AddGroupProps {
  /** External control: modal open state */
  isOpen?: boolean
  /** External control: modal open change handler (toggles state) */
  onOpenChange?: () => void
  /** Show the trigger button (default: true for desktop, false for mobile) */
  showButton?: boolean
}

export const AddGroup = ({
  isOpen: externalIsOpen,
  onOpenChange: externalOnOpenChange,
  showButton = true,
}: AddGroupProps) => {
  const internalDisclosure = useDisclosure()

  // Use external control if provided, otherwise use internal
  const isOpen = externalIsOpen ?? internalDisclosure.isOpen
  const onOpen = internalDisclosure.onOpen
  const onOpenChange = externalOnOpenChange ?? internalDisclosure.onOpenChange
  const onClose = externalOnOpenChange ?? internalDisclosure.onClose

  const { groupName, setNameBorderColor, reset, isSubmitting, setIsSubmitting } =
    useHomeAddGroupStore()
  const { loadGroups } = useGroupStore()

  useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen, reset])

  const handleSubmit = async () => {
    // Validation
    setNameBorderColor()

    if (!groupName || groupName.includes('$') || groupName.length > 20) {
      return
    }

    setIsSubmitting(true)

    try {
      await createGroup({ name: groupName })
      // Reload groups
      await loadGroups()

      // Close modal and reset
      onClose()
      reset()
    } catch (error) {
      console.error('Failed to create group:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      {showButton && (
        <Button
          onPress={onOpen}
          className="font-bold bg-tdplbisque text-tdplprussian mx-2 text-[1.25rem] hover:bg-tdpllightgrey hover:text-tdplindigo"
          data-testid="add-group-button"
        >
          Add Group
        </Button>
      )}
      <BaseModal isOpen={isOpen} onOpenChange={onOpenChange} size={'3xl'} isDismissable={true}>
        <ModalContent className="overflow-x-hidden">
          {(onClose) => (
            <>
              <BaseModalHeader title="Add Group" description={ModalText('Add Group')} />
              <ModalBody className="gap-1 min-h-[8rem] pl-6 sm:pl-10 pr-6 sm:pr-10 overflow-x-hidden overflow-y-auto">
                <BaseDivider />
                <InputGroupName />
              </ModalBody>
              <ModalFooter className="pb-6 sm:pb-8 pl-6 sm:pl-10 pr-6 sm:pr-10">
                <div className="flex gap-3 justify-end w-full">
                  <Button
                    variant="light"
                    className="font-bold text-tdplindigo border border-tdplindigo"
                    onPress={onClose}
                    isDisabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="font-bold text-white w-[6rem] mr-0 bg-tdplplum"
                    onPress={handleSubmit}
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                  >
                    Create
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </BaseModal>
    </div>
  )
}
